import { LitElement, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { validateSlotContent } from '../utils/slot-validator';
import { styles } from './sds-card.styles';

export interface SDSCardProps {
	link?: string;
}

@guardedCustomElement('sds-card')
export class SDSCard extends LitElement {
	// Styling
	static override styles = [styles];

	// Reactive Props
	@property({ type: String, reflect: true }) link: SDSCardProps['link'];

	// Slots
	@query('slot#heading') private headingSlot!: HTMLSlotElement;
	@query('slot#text') private textSlot!: HTMLSlotElement;

	private _contentWarning: string = '[sds-card] text slot should only contain top-level <p> elements with no nested elements:';
	private _headerWarning: string = '[sds-card] header slot should only contain one of <h2>–<h6> elements:';

	protected override firstUpdated(): void {
		validateSlotContent(this.textSlot, ['P'], false, this._contentWarning);

		const headerNodes = this.headingSlot.assignedNodes({ flatten: true });
		if (headerNodes.length > 0) {
			validateSlotContent(this.headingSlot, ['H2', 'H3', 'H4', 'H5', 'H6'], false, this._headerWarning);
		}
	}

	private readonly _handleHeadingSlotChange = () => {
		const nodes = this.headingSlot.assignedNodes({ flatten: true });
		if (nodes.length > 0) {
			validateSlotContent(this.headingSlot, ['H2', 'H3', 'H4', 'H5', 'H6'], false, this._headerWarning);
		}
	};

	private readonly _handleTextSlotChange = () => {
		validateSlotContent(this.textSlot, ['P'], false, this._contentWarning);
	};

	override render() {
		const cardBody = html`
			<slot name="heading" id="heading" @slotchange=${this._handleHeadingSlotChange}></slot>
			<slot name="text" id="content" @slotchange=${this._handleTextSlotChange}></slot>
			<sds-icon name="arrow_forward-material-outlined" size="md" class="sds-card__icon"></sds-icon>
		`;

		return html`<article class="sds-card">${this.link ? html`<a href="${this.link}" class="sds-card__content">${cardBody}</a>` : html`<div class="sds-card__content">${cardBody}</div>`}</article>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-card': SDSCard;
	}
}
